import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, updateDoc, onSnapshot, increment } from 'firebase/firestore';
import { db } from '../firebase';

// Updated PLANS configuration with PDF limits
export const PLANS = {
  FREE: {
    name: "Free",
    price: "0",
    period: "Forever",
    description: "Perfect for getting started",
    features: [
      "3 QR codes per month",
      "1 PDF upload per month", // ✅ NEW
      "1 Dynamic QR code",
      "Static QR support",
      "Custom color and logo options",
      "Download in PNG or SVG"
    ],
    limits: {
      qrPerMonth: 3,
      pdfUploadsPerMonth: 1, // ✅ NEW: 1 PDF allowed
      dynamicQR: 3,
      creditsPerHour: null,
      isUnlimited: false,
      unlimitedQRFromPDF: true // ✅ NEW: QR codes from PDF don't count toward limit
    },
    features_config: {
      dynamicQR: true
    },
    cta: "Start Free",
    popular: false,
    accent: "free",
    stripeKey: null
  },

  PRO: {
    name: "Pro",
    price: "6.99",
    period: "per month",
    description: "For professionals and small teams",
    features: [
      "100 QR codes per month",
      "5 PDF uploads per month", // ✅ NEW
      "Static QR codes",
      "Dynamic & Trackable QR codes",
      "5 QR/hour limit",
      "Download in PNG, SVG, or PDF"
    ],
    limits: {
      qrPerMonth: 100,
      pdfUploadsPerMonth: 5, // ✅ NEW
      dynamicQR: -1,
      creditsPerHour: 5,
      isUnlimited: false,
      unlimitedQRFromPDF: true
    },
    features_config: {
      dynamicQR: true
    },
    cta: "Upgrade to Pro",
    popular: true,
    accent: "pro",
    stripeKey: "Pro"
  },

  PREMIUM: {
    name: "Premium",
    price: "19.99",
    period: "per month",
    description: "For businesses and agencies",
    features: [
      "400 QR codes per month",
      "Unlimited PDF uploads", // ✅ NEW
      "Unlimited Dynamic QR codes",
      "Advanced analytics and tracking",
      "5 QR/hour limit",
      "Download in all formats"
    ],
    limits: {
      qrPerMonth: 400,
      pdfUploadsPerMonth: -1, // ✅ NEW: -1 = unlimited
      dynamicQR: -1,
      creditsPerHour: 5,
      isUnlimited: false,
      unlimitedQRFromPDF: true
    },
    features_config: {
      dynamicQR: true
    },
    cta: "Go Premium",
    popular: false,
    accent: "premium",
    stripeKey: "Premium"
  }
};

export const usePlanTracker = (userId) => {
  const [currentPlan, setCurrentPlan] = useState("FREE");
  const [usage, setUsage] = useState({
    qrGenerated: 0,
    dynamicQRGenerated: 0,
    pdfUploadsUsed: 0, // ✅ NEW: Track PDF uploads
    lastReset: null,
    hourlyUsage: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState("free");

  const TEST_MODE = false;
  const planConfig = PLANS[currentPlan.toUpperCase()] || PLANS.FREE;

  const resetIfNewMonth = useCallback(async (usageRef, data) => {
    const now = new Date();
    const lastReset = data.lastReset ? new Date(data.lastReset) : null;

    if (!lastReset || now.getMonth() !== lastReset.getMonth()) {
      await updateDoc(usageRef, {
        qrGenerated: 0,
        dynamicQRGenerated: 0,
        pdfUploadsUsed: 0, // ✅ Reset PDF counter monthly
        lastReset: now.toISOString(),
        hourlyUsage: []
      });
      setUsage({
        qrGenerated: 0,
        dynamicQRGenerated: 0,
        pdfUploadsUsed: 0,
        lastReset: now,
        hourlyUsage: []
      });
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    if (TEST_MODE) {
      setLoading(false);
      return;
    }

    const usageRef = doc(db, "usage", userId);
    const userRef = doc(db, "users", userId);

    const unsubUsage = onSnapshot(usageRef, async (snapshot) => {
      if (!snapshot.exists()) {
        await setDoc(usageRef, {
          qrGenerated: 0,
          dynamicQRGenerated: 0,
          pdfUploadsUsed: 0, // ✅ Initialize
          lastReset: new Date().toISOString(),
          hourlyUsage: []
        });
      } else {
        const data = snapshot.data();
        await resetIfNewMonth(usageRef, data);
        setUsage(data);
      }
      setLoading(false);
    });

    const unsubUser = onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setCurrentPlan(data.plan?.toUpperCase() || "FREE");
        setSubscriptionStatus(data.subscriptionStatus || "active");
      }
    });

    return () => {
      unsubUsage();
      unsubUser();
    };
  }, [userId, resetIfNewMonth, TEST_MODE]);

  // ✅ NEW: Consume PDF upload credit
  const consumePDFUpload = async () => {
    if (TEST_MODE) return true;

    const usageRef = doc(db, "usage", userId);
    const usageSnap = await getDoc(usageRef);

    if (!usageSnap.exists()) return false;

    const data = usageSnap.data();
    const newPDFTotal = (data.pdfUploadsUsed || 0) + 1;

    await updateDoc(usageRef, {
      pdfUploadsUsed: increment(1)
    });

    setUsage((prev) => ({
      ...prev,
      pdfUploadsUsed: newPDFTotal
    }));

    return true;
  };

  // ✅ UPDATED: Add flag for PDF-generated QR codes
  const consumeCredit = async (isDynamic = false, isFromPDF = false) => {
    if (TEST_MODE) return true;

    // ✅ If QR is from PDF and plan allows unlimited QR from PDF, don't consume credit
    if (isFromPDF && planConfig.limits.unlimitedQRFromPDF) {
      console.log('QR from PDF - not consuming credit');
      return true;
    }

    const usageRef = doc(db, "usage", userId);
    const usageSnap = await getDoc(usageRef);

    if (!usageSnap.exists()) return false;

    const data = usageSnap.data();
    const now = new Date();
    const currentHour = now.getHours();

    const updatedHourly = data.hourlyUsage.filter(
      (entry) => new Date(entry.time).getHours() === currentHour
    );

    updatedHourly.push({ time: now.toISOString() });

    const newTotal = (data.qrGenerated || 0) + 1;
    const newDynamicTotal = isDynamic ? (data.dynamicQRGenerated || 0) + 1 : (data.dynamicQRGenerated || 0);

    await updateDoc(usageRef, {
      qrGenerated: increment(1),
      ...(isDynamic && { dynamicQRGenerated: increment(1) }),
      hourlyUsage: updatedHourly
    });

    setUsage((prev) => ({
      ...prev,
      qrGenerated: newTotal,
      dynamicQRGenerated: newDynamicTotal,
      hourlyUsage: updatedHourly
    }));

    return true;
  };

  // ✅ NEW: Check if user can upload PDF
  const canUploadPDF = () => {
    const pdfLimit = planConfig.limits.pdfUploadsPerMonth;
    
    // -1 means unlimited
    if (pdfLimit === -1) return true;
    
    const pdfUsed = usage.pdfUploadsUsed || 0;
    return pdfUsed < pdfLimit;
  };

  const canGenerateQR = () => {
    if (planConfig.limits.isUnlimited) return true;
    if (usage.qrGenerated >= planConfig.limits.qrPerMonth)
      return false;
    if (
      planConfig.limits.creditsPerHour &&
      usage.hourlyUsage.length >= planConfig.limits.creditsPerHour
    )
      return false;
    return true;
  };

  const canGenerateDynamicQR = () => {
    const dynamicLimit = planConfig.limits.dynamicQR;
    if (dynamicLimit === -1) return true;
    const dynamicUsed = usage.dynamicQRGenerated || 0;
    return dynamicUsed < dynamicLimit;
  };

  const getLimitReason = () => {
    if (usage.qrGenerated >= planConfig.limits.qrPerMonth)
      return "Monthly QR limit reached.";
    if (
      planConfig.limits.creditsPerHour &&
      usage.hourlyUsage.length >= planConfig.limits.creditsPerHour
    )
      return "Hourly limit reached. Please wait.";
    return null;
  };

  const getUsageStats = () => {
    const remaining =
      planConfig.limits.isUnlimited
        ? "Unlimited"
        : planConfig.limits.qrPerMonth - usage.qrGenerated;

    const dynamicLimit = planConfig.limits.dynamicQR;
    const dynamicUsed = usage.dynamicQRGenerated || 0;
    const dynamicRemaining = dynamicLimit === -1 ? "Unlimited" : dynamicLimit - dynamicUsed;

    // ✅ NEW: PDF upload stats
    const pdfLimit = planConfig.limits.pdfUploadsPerMonth;
    const pdfUsed = usage.pdfUploadsUsed || 0;
    const pdfRemaining = pdfLimit === -1 ? "Unlimited" : pdfLimit - pdfUsed;

    return {
      qrGenerated: usage.qrGenerated,
      qrRemaining: remaining,
      dynamicQRGenerated: dynamicUsed,
      dynamicQRRemaining: dynamicRemaining,
      dynamicQRLimit: dynamicLimit,
      pdfUploadsUsed: pdfUsed, // ✅ NEW
      pdfUploadsRemaining: pdfRemaining, // ✅ NEW
      pdfUploadsLimit: pdfLimit, // ✅ NEW
      hourlyUsage: usage.hourlyUsage.length,
      resetDate: usage.lastReset,
      canGenerate: canGenerateQR(),
      canGenerateDynamic: canGenerateDynamicQR(),
      canUploadPDF: canUploadPDF(), // ✅ NEW
      subscriptionStatus
    };
  };

  const updatePlan = async (plan) => {
    if (TEST_MODE) {
      setCurrentPlan(plan);
      return;
    }

    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, { plan: plan.toLowerCase() });
    setCurrentPlan(plan.toUpperCase());
  };

  const cancelSubscription = async () => {
    if (TEST_MODE) {
      setCurrentPlan("FREE");
      return;
    }

    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, { plan: "free" });
    setCurrentPlan("FREE");
  };

  return {
    currentPlan,
    planConfig,
    usage,
    loading,
    error,
    subscriptionStatus,

    consumeCredit,
    consumePDFUpload, // ✅ NEW
    canGenerateQR,
    canGenerateDynamicQR,
    canUploadPDF, // ✅ NEW
    getLimitReason,
    getUsageStats,
    updatePlan,
    cancelSubscription
  };
};